import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes'

import { DifficultForm } from 'widgets/avia/difficult-form'
import { difficultRouteSchema } from 'widgets/avia/difficult-form/lib/checkDifficultRoute'
import LevelClassNCount, { CountAndClass } from 'widgets/level-class-count'

import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'

import { MAX_COUNT_DIFFICULT_ROUTE, TicketClassList } from 'shared/constants'
import { IErrorForm, getFieldsError } from 'shared/lib/validate'
import { AllIcons } from 'shared/ui/app-icon/types'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { Button } from 'shared/ui/button'
import { ButtonIconText } from 'shared/ui/button-icon-text'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Paper } from 'shared/ui/paper'

import s from './styles.module.scss'

const DifficultRoute = observer(() => {
  const { t } = useTranslation()

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const navigate = useNavigate()

  const totalCountPassengers = findTicketFormStore.getCountOfPassengers()

  const [errors, setErrors] = useState<IErrorForm>()

  const result = useMemo(
    () =>
      TicketClassList?.find(
        (item) => item?.key === findTicketFormStore.formFindTicket?.cabin_class,
      ),
    [],
  )

  const onShowCabin = () => {
    openDialog({
      child: (
        <LevelClassNCount
          directions={toJS(findTicketFormStore?.formFindTicket?.directions)}
          data={{
            adult: findTicketFormStore.formFindTicket.adult,
            infant: findTicketFormStore.formFindTicket.infant,
            infants_with_place:
              findTicketFormStore.formFindTicket.infants_with_place,
            child: findTicketFormStore.formFindTicket.child,
            cabin_class: String(findTicketFormStore.formFindTicket.cabin_class),
          }}
          onChangeCountHandler={onSubmitCountAndClass}
        />
      ),
      maxHeight: '95vh',
    })
  }

  const onSubmitCountAndClass = (data?: CountAndClass) => {
    findTicketFormStore.onSetCountAndClass(data)

    closeDialog()
  }

  const onAddDifficultRoute = () => {
    findTicketFormStore.onAddDirection()
  }

  const onDeleteDifficult = (index: number) => {
    findTicketFormStore.deleteDifficultRoute(index)
  }

  const onSwitchDirection = (index: number) => {
    findTicketFormStore.onSwitchDirection(index)
  }

  const validateForm = async () => {
    try {
      await difficultRouteSchema.validate(findTicketFormStore?.formFindTicket, {
        abortEarly: false,
      })
      onGoSearch?.()
    } catch (error) {
      const resultValidate = getFieldsError(error)

      setErrors(resultValidate)
    }
  }

  const onGoSearch = () => {
    findTicketFormStore.onGoToDifficultSearch()

    navigate(RoutingPaths.avia.search.path())
  }

  const onGoBack = () => {
    navigate(-1)
  }

  const lastSelectedDate =
    findTicketFormStore?.formFindTicket?.directions?.at(-1)?.date

  return (
    <div className={clsx('bgGrey', s.page)}>
      <HeaderGoBack
        onGoBack={onGoBack}
        title={t('difficult_route')}
        backgroundColor="#eff1f4"
        buttonWhite
      />

      <div className={s.content}>
        <Paper
          padding={[12]}
          radius="24px"
          className={s.passengersCard}
        >
          <div className={s.passengerField}>
            <ButtonIconText
              type="lightGrey"
              radius={16}
              height="100%"
              onClick={onShowCabin}
              placeholder={t('passengers_and_class')}
              rightIcon={AllIcons.PASSENGERS_BG}
              value={`${totalCountPassengers}, ${t(result?.name || '')}`}
            />
          </div>
        </Paper>

        <DifficultForm
          onDeleteForm={onDeleteDifficult}
          onSwitchDirection={onSwitchDirection}
          formList={findTicketFormStore?.formFindTicket?.directions}
          errors={errors}
        />

        {MAX_COUNT_DIFFICULT_ROUTE !==
          findTicketFormStore?.formFindTicket?.directions?.length &&
          lastSelectedDate && (
            <Button
              className={s.addButton}
              variant="grey"
              borderRadius="nr"
              onClick={onAddDifficultRoute}
            >
              {t('transfer_add_direction')}
            </Button>
          )}
      </div>

      <ButtonContinue
        onClick={validateForm}
        borderRadius="md"
      >
        <span className={s.text}>{t('find_tickets')}</span>
      </ButtonContinue>
    </div>
  )
})

export default DifficultRoute
